import React, { useState } from "react";

type MetricChange = {
  value: number;
  changeFromPrev: { abs: number; pct: number };
  changeFromLastYear: { abs: number; pct: number };
};

type GSCRow = {
  query: string;
  page: string;
  country: string;
  device: string;
  clicks: MetricChange;
  impressions: MetricChange;
  ctr: MetricChange;
  position: MetricChange;
};

interface GSCTableProps {
  data: GSCRow[];
}

type SortKey = "query" | "clicks" | "impressions" | "ctr" | "position";

export const GSCQueryTable: React.FC<GSCTableProps> = ({ data }) => {
  const [expandedQueries, setExpandedQueries] = useState<Set<string>>(
    new Set()
  );
  const [sortKey, setSortKey] = useState<SortKey>("impressions");
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  const toggleQuery = (query: string) => {
    const newSet = new Set(expandedQueries);
    if (expandedQueries.has(query)) newSet.delete(query);
    else newSet.add(query);
    setExpandedQueries(newSet);
  };

  const colorClass = (pct: number) =>
    pct > 0 ? "text-green-600" : pct < 0 ? "text-red-600" : "text-gray-500";

  // Group rows by query
  const grouped: Record<string, GSCRow[]> = data.reduce((acc, row) => {
    if (!acc[row.query]) acc[row.query] = [];
    acc[row.query].push(row);
    return acc;
  }, {} as Record<string, GSCRow[]>);

  // Convert grouped object to array for sorting
  const groupedArray = Object.entries(grouped).map(([query, rows]) => {
    const totalClicks = rows.reduce(
      (sum, r) => sum + (r.clicks?.value || 0),
      0
    );
    const totalImpressions = rows.reduce(
      (sum, r) => sum + (r.impressions?.value || 0),
      0
    );
    const avgCtr = totalClicks / (totalImpressions || 1);
    const avgPosition =
      rows.reduce((sum, r) => sum + (r.position?.value || 0), 0) /
      (rows.length || 1);

    return { query, rows, totalClicks, totalImpressions, avgCtr, avgPosition };
  });

  // Sort grouped queries
  const sortedGrouped = [...groupedArray].sort((a, b) => {
    let aVal: string | number = 0;
    let bVal: string | number = 0;

    switch (sortKey) {
      case "query":
        aVal = a.query.toLowerCase();
        bVal = b.query.toLowerCase();
        break;
      case "clicks":
        aVal = a.totalClicks;
        bVal = b.totalClicks;
        break;
      case "impressions":
        aVal = a.totalImpressions;
        bVal = b.totalImpressions;
        break;
      case "ctr":
        aVal = a.avgCtr;
        bVal = b.avgCtr;
        break;
      case "position":
        aVal = a.avgPosition;
        bVal = b.avgPosition;
        break;
    }

    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortAsc
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  // Group rows by device for expanded view
  const groupByDevice = (rows: GSCRow[]) => {
    const deviceGroups: Record<
      string,
      {
        pages: string;
        clicks: MetricChange;
        impressions: MetricChange;
        ctr: MetricChange;
        position: MetricChange;
        count: number;
      }
    > = {};

    rows.forEach((row) => {
      const device = row.device;
      if (!deviceGroups[device]) {
        deviceGroups[device] = {
          pages: row.page,
          clicks: { ...row.clicks },
          impressions: { ...row.impressions },
          ctr: { ...row.ctr },
          position: { ...row.position },
          count: 1,
        };
      } else {
        const group = deviceGroups[device];
        group.count += 1;

        // Sum values
        group.clicks.value += row.clicks.value;
        group.impressions.value += row.impressions.value;

        // Average percentages
        group.clicks.changeFromPrev.pct =
          (group.clicks.changeFromPrev.pct * (group.count - 1) +
            row.clicks.changeFromPrev.pct) /
          group.count;
        group.clicks.changeFromLastYear.pct =
          (group.clicks.changeFromLastYear.pct * (group.count - 1) +
            row.clicks.changeFromLastYear.pct) /
          group.count;

        group.impressions.changeFromPrev.pct =
          (group.impressions.changeFromPrev.pct * (group.count - 1) +
            row.impressions.changeFromPrev.pct) /
          group.count;
        group.impressions.changeFromLastYear.pct =
          (group.impressions.changeFromLastYear.pct * (group.count - 1) +
            row.impressions.changeFromLastYear.pct) /
          group.count;

        // Average ctr and position
        group.ctr.value =
          (group.ctr.value * (group.count - 1) + row.ctr.value) / group.count;
        group.position.value =
          (group.position.value * (group.count - 1) + row.position.value) /
          group.count;
      }
    });

    return Object.entries(deviceGroups).map(([device, group]) => ({
      device,
      pages: group.pages,
      clicks: group.clicks,
      impressions: group.impressions,
      ctr: group.ctr,
      position: group.position,
    }));
  };

  return (
    <div className="mt-8">
      <div className="overflow-x-auto">
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full table-auto text-sm border-collapse">
            <thead className="text-left text-muted-foreground cursor-pointer sticky top-0 bg-white z-10">
              <tr className="border-b">
                {["query", "clicks", "impressions", "ctr", "position"].map(
                  (col) => (
                    <th
                      key={col}
                      className="px-4 py-2 text-left bg-white"
                      onClick={() => handleSort(col as SortKey)}
                    >
                      {col.charAt(0).toUpperCase() + col.slice(1)}
                      {sortKey === col && (sortAsc ? " ↑" : " ↓")}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {sortedGrouped.map(
                ({
                  query,
                  rows,
                  totalClicks,
                  totalImpressions,
                  avgCtr,
                  avgPosition,
                }) => {
                  const isExpanded = expandedQueries.has(query);
                  return (
                    <React.Fragment key={query}>
                      <tr
                        className="border-b cursor-pointer hover:bg-muted/30 font-medium"
                        onClick={() => toggleQuery(query)}
                      >
                        <td className="px-4 py-2">{query}</td>
                        <td className="px-4 py-2">{totalClicks}</td>
                        <td className="px-4 py-2">
                          {totalImpressions.toLocaleString()}
                        </td>
                        <td className="px-4 py-2">
                          {(avgCtr * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-2">{avgPosition.toFixed(0)}</td>
                      </tr>

                      {isExpanded &&
                        groupByDevice(rows).map((row, idx) => (
                          <tr
                            key={idx}
                            className="border-b last:border-0 bg-gray-50 hover:bg-gray-100"
                          >
                            <td className="px-4 py-2 break-words flex items-center space-x-2">
                              <span>{row.pages}</span>
                              <span
                                className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  row.device === "MOBILE"
                                    ? "bg-blue-100 text-blue-800"
                                    : row.device === "DESKTOP"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {row.device}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {row.clicks.value} (
                              <span
                                className={colorClass(
                                  row.clicks.changeFromPrev.pct
                                )}
                              >
                                {row.clicks.changeFromPrev.pct.toFixed(1)}% PoP
                              </span>
                              ,{" "}
                              <span
                                className={colorClass(
                                  row.clicks.changeFromLastYear.pct
                                )}
                              >
                                {row.clicks.changeFromLastYear.pct.toFixed(1)}%
                                YoY
                              </span>
                              )
                            </td>
                            <td className="px-4 py-2">
                              {row.impressions.value} (
                              <span
                                className={colorClass(
                                  row.impressions.changeFromPrev.pct
                                )}
                              >
                                {row.impressions.changeFromPrev.pct.toFixed(1)}%
                                PoP
                              </span>
                              ,{" "}
                              <span
                                className={colorClass(
                                  row.impressions.changeFromLastYear.pct
                                )}
                              >
                                {row.impressions.changeFromLastYear.pct.toFixed(
                                  1
                                )}
                                % YoY
                              </span>
                              )
                            </td>
                            <td className="px-4 py-2">
                              {(row.ctr.value * 100).toFixed(1)}%
                            </td>
                            <td className="px-4 py-2">
                              {row.position.value.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
